import { type ReactNode } from 'react';
import { LICENSE_LABELS, LICENSE_TYPE, type LicenseType } from '@/config';
import { getUser, getUserPermission } from '@/features/auth/api/getUser';
import {
  type LoginCredentials,
  loginWithEmailAndPassword,
} from '@/features/auth/api/login';
import { logout } from '@/features/auth/api/logout';
import {
  type RegisterCredentials,
  register,
} from '@/features/auth/api/register';
import { type AuthUser } from '@/features/auth/types';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import { queryClient } from '@/lib/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

const authUserQueryKey = ['auth-user'] as const;
const userPermissionQueryKey = ['auth-user-permission'] as const;

async function userFn() {
  return await getUser();
}

async function userPermissionFn() {
  return await getUserPermission();
}

async function registerFn(data: RegisterCredentials) {
  return await register(data);
}

async function loginFn(data: LoginCredentials) {
  return await loginWithEmailAndPassword(data);
}

async function logoutFn() {
  await logout(getCurrentLanguage());
}

export const useUser = () => {
  return useQuery({
    queryKey: authUserQueryKey,
    queryFn: userFn,
    retry: false,
  });
};

export const useLogin = () => {
  return useMutation({
    mutationFn: loginFn,
    onSuccess: (user) => {
      queryClient.setQueryData(authUserQueryKey, user);
      void queryClient.invalidateQueries({ queryKey: userPermissionQueryKey });
    },
  });
};

export const useLogout = () => {
  return useMutation({
    mutationFn: async (variables: Record<string, never> = {}) => {
      void variables;
      await logoutFn();
      return null;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: authUserQueryKey });
      queryClient.setQueryData<AuthUser | null>(authUserQueryKey, null);
      queryClient.removeQueries({ queryKey: userPermissionQueryKey });
    },
  });
};

export const AuthLoader = ({
  children,
  renderLoading,
  renderUnauthenticated,
}: {
  children: ReactNode;
  renderLoading: () => ReactNode;
  renderUnauthenticated: () => ReactNode;
}) => {
  const { data: user, isLoading } = useUser();

  if (isLoading) return renderLoading();
  if (user == null) return renderUnauthenticated();

  return children;
};

// register すると user が reactQuery によってキャッシュされ、瞬ログイン状態みたいになってバグるので、
// react query auth の useRegister は使わない
export const useRegister = () => {
  return useMutation({
    mutationFn: registerFn,
  });
};

export const useUserPermission = () => {
  const query = useQuery({
    queryKey: userPermissionQueryKey,
    queryFn: userPermissionFn,
    staleTime: 1000 * 60 * 3,
  });

  const summarizedRole = query.data?.summarizedRole;
  const grantedLicenseTypes = query.data?.grantedLicenseTypes;
  const isOnlyBasicLicense = onlyHasLicense(
    grantedLicenseTypes,
    LICENSE_TYPE.BASIC
  );
  const hasEduLicense = Boolean(
    grantedLicenseTypes?.includes(LICENSE_TYPE.EDU)
  );
  const isSystemAdmin = summarizedRole === 'system_admin';
  const licenseLabels = (grantedLicenseTypes ?? [])
    .map((type) => LICENSE_LABELS[type])
    .filter(Boolean);

  return {
    ...query,
    isOnlyBasicLicense,
    hasEduLicense,
    isSystemAdmin,
    licenseLabels,
  };
};

const onlyHasLicense = (
  grantedLicenseTypes: readonly LicenseType[] | undefined,
  licenseType: LicenseType
) => {
  return (
    grantedLicenseTypes?.length === 1 && grantedLicenseTypes[0] === licenseType
  );
};
